import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from "react-hook-form";
import { useFormContext } from "../../../context/Context";
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useHolidayListContext } from "../../../context/HolidayListContext";

export default function AssetRequestForm() {
    const navigate = useNavigate();
    const {
        register, handleSubmit, onSubmit, reset, formState: { errors }, clearErrors, setValue, control, setError
    } = useForm();
    const {setServerError} = useFormContext();
    const [assetRequestDateColor, setAssetRequestDateColor] = useState("#d3d3d3");
    const [assetTypeColor, setAssetTypeColor] = useState("#d3d3d3");
    const [customErrorForAssetInputs, setCustomErrorForAssetInputs] = useState({
        assetEmpName: '',
        assetName: '',
        requestReason: ''
    });
    const watchedValues = useWatch({
        control,
        name: ["assetEmpName", "assetName", "assetRequestDate", "assetType", "requestReason"],
    });
    const [assetEmpName, assetName, assetRequestDate, assetType, requestReason] = watchedValues;

    const hasErrors = Object.keys(errors).length > 0;
    const isApplyAssetRequestDisabled = !assetEmpName || !assetName || !assetRequestDate || !assetType || !requestReason || hasErrors;
    const isCancelAssetRequestDisabled = !assetEmpName && !assetName && !assetRequestDate && !assetType && !requestReason;

    const holidays = useHolidayListContext();

    const typeOfAsset =
        ["Laptop", "Desktop Computer", "Monitor", "Keyboard", "Mouse", "Printer", "Scanner", "External Hard Drive",
            "Headphone"];

    // Convert holiday dates to YYYY-MM-DD format
    const formattedHolidays = useMemo(() => {
        const monthMap = {
            Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
            Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
        };
        const year = new Date().getFullYear();

        // Using a Set here
        return new Set(holidays.map((holiday) => {
            const [month, day] = holiday.date.split(" ");
            return `${year}-${monthMap[month]}-${day.padStart(2, "0")}`;
        }));
    }, [holidays]);

    // Function to check if a date is a holiday or weekend
    const isDisabledFromDate = (date) => {
        const day = new Date(date).getDay(); // 0 = Sunday, 6 = Saturday
        return formattedHolidays.has(date) || day === 0 || day === 6;
    };

    const handlePatternForAssetInputs = (e, pattern, field) => {
        let value = e.target.value;
        setServerError('');
        clearErrors(field);
        setValue(field, value);
        let patternErrorMessage = '';
        if (field === 'assetEmpName' && value && !pattern.test(value)) {
            patternErrorMessage = 'No numbers or special characters are allowed';
            setError(field, { type: "pattern", message: patternErrorMessage });
        } else if ((field === 'assetName' || field === 'requestReason') && value && !pattern.test(value)) {
            patternErrorMessage = 'No special characters are allowed';
            setError(field, { type: "pattern", message: patternErrorMessage });
        } else {
            clearErrors(field);
        }
        setCustomErrorForAssetInputs(prev => ({ ...prev, [field]: patternErrorMessage }));
        if (!patternErrorMessage) {
            setCustomErrorForAssetInputs(prev => ({ ...prev, [field]: '' }));
            clearErrors(field);
        }
    };

    const handleAssetRequestDateColorChange = (e) => {
        const selectedAssetRequestDateValue = e.target.value;
        if (isDisabledFromDate(selectedAssetRequestDateValue)) {
            alert("This date is a holiday or a weekend. Please select another date.");
            setValue("assetRequestDate", "");
            setAssetRequestDateColor("#d3d3d3");
        } else {
            setValue("assetRequestDate", selectedAssetRequestDateValue);
            setAssetRequestDateColor("black");
        }
        clearErrors("assetRequestDate");
    };
    const handleAssetTypeColorChange = (e) => {
        const selectedAssetTypeValue = e.target.value;
        setValue("assetType", selectedAssetTypeValue);
        setAssetTypeColor(selectedAssetTypeValue ? "black" : "#d3d3d3");
        clearErrors('assetType');
    };
    const handleCancelAssetRequest = () => {
        reset();
        setAssetTypeColor("#d3d3d3");
        setAssetRequestDateColor("#d3d3d3");
        clearErrors();
    };
    const handleFormSubmit = (data) => {
        if (Object.keys(errors).length > 0) {
            return;
        }
        onSubmit(data);
        reset();
        setTimeout(() => {
            navigate('/admin/dashboard');
        }, 2000);
        toast.success("Your request for asset has been submitted");
        setServerError('');
        clearErrors();
        setAssetTypeColor("#d3d3d3");
        setAssetRequestDateColor("#d3d3d3");
    };

    const assetEmpNameErrMsg = errors.assetEmpName?.message || customErrorForAssetInputs.assetEmpName;
    const assetNameErrMsg = errors.assetName?.message || customErrorForAssetInputs.assetName;
    const requestReasonErrMsg = errors.requestReason?.message || customErrorForAssetInputs.requestReason;

    return (
        <div className='container-fluid'>
            <div className='row clearfix'>
                <div className='col-md-12'>
                    <Breadcrumb />
                    <div className='attendance-container' style={{ padding: '0', marginBottom: '45px', marginTop: '35px' }}>
                        <form onSubmit={handleSubmit(handleFormSubmit)}>
                            <h4 className='apply-leave-heading'>Asset Request Form</h4>
                            <div className='apply-leave-section'>
                                <div className="input-text">
                                    <p className='applyLeaveLabel'>Employee Name<span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <input className="input-field" type="text"
                                            {...register("assetEmpName", {
                                                required: true,
                                                maxLength: {
                                                    value: 50,
                                                    message: 'Name cannot exceed 50 characters'
                                                },
                                                minLength: {
                                                    value: 3,
                                                    message: 'Name must be at least 3 characters'
                                                }
                                            })}
                                            onChange={(e) => handlePatternForAssetInputs(e, /^[A-Za-z\s]+$/, 'assetEmpName')} />
                                            {assetEmpNameErrMsg && <div className="errorMessage">{assetEmpNameErrMsg}</div>}
                                    </div>
                                </div>
                                <div className="input-text">
                                    <p className='applyLeaveLabel'>Type Of Asset<span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <select className="input-field" {...register("assetType", { required: true })}
                                            onChange={(e) => handleAssetTypeColorChange(e)} style={{ color: assetTypeColor }}>
                                            <option value="" hidden style={{ color: "#d3d3d3" }}>Choose the asset type</option>
                                            {typeOfAsset.map((option) => (
                                                <option key={option} style={{ color: 'black' }}>{option}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="input-text">
                                    <p className='applyLeaveLabel'>Asset Name<span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <input className="input-field" type="text"
                                            {...register("assetName", {
                                                required: true,
                                                maxLength: {
                                                    value: 100,
                                                    message: 'Asset name cannot exceed 100 characters'
                                                },
                                                minLength: {
                                                    value: 3,
                                                    message: 'Asset name must be at least 3 characters'
                                                }
                                            })}
                                            onChange={(e) => handlePatternForAssetInputs(e, /^[a-zA-Z0-9_\s-]+$/, 'assetName')} />
                                            {assetNameErrMsg && <div className="errorMessage">{assetEmpNameErrMsg}</div>}
                                    </div>
                                </div>
                                <div className="input-text">
                                    <p className='applyLeaveLabel'>Request Date<span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <input className="input-field" type="date"
                                            {...register("assetRequestDate", {
                                                required: true,
                                            })} onChange={(e) => handleAssetRequestDateColorChange(e)} style={{ color: assetRequestDateColor, padding: '5px' }} />
                                    </div>
                                </div>
                                <div className="input-text" style={{ marginTop: '5px' }}>
                                    <p className='applyLeaveLabel'>Reason Of Request <span style={{ color: "red" }}>*</span></p>
                                    <div className="user-input-icons">
                                        <textarea rows="4" className="input-field" {...register("requestReason", {
                                            required: true,
                                            maxLength: {
                                                value: 100,
                                                message: 'Reason Of request cannot exceed 100 characters'
                                            },
                                            minLength: {
                                                value: 3,
                                                message: 'Reason Of request must be at least 3 characters'
                                            }
                                        })} onChange={(e) => handlePatternForAssetInputs(e, /^[a-zA-Z0-9\s.,!'"()&-]+$/, 'requestReason')}>
                                        </textarea>
                                        {requestReasonErrMsg && <div className="errorMessage">{assetEmpNameErrMsg}</div>}
                                    </div>
                                </div>
                                <div className="input-text" style={{ marginTop: '-6px' }}>
                                    <p className='applyLeaveLabel'>Attach doc(if any)</p>
                                    <div>
                                        <input className="input-field" type="file"
                                            {...register("assetDoc", {
                                                required: true,
                                            })} />
                                        {errors.assetDoc && (<div className="userErrorMessage">{errors.assetDoc.message}</div>)}
                                    </div>
                                </div>
                                <div className='edit-delete-btn-container' style={{ marginTop: '10px' }}>
                                    <button className="primary-btn" style={{ background: 'darkgray', width: '100px' }} disabled={isCancelAssetRequestDisabled} onClick={() => handleCancelAssetRequest()}>Cancel</button>
                                    <button className="primary-btn" type="submit" style={{ width: '100px' }} disabled={isApplyAssetRequestDisabled} >Apply</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </div >
    )
}
